import React from 'react';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '20px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '16px',
  },
  column: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  columnBar: {
    height: '3px',
  },
  columnHeader: {
    padding: '14px 16px 10px',
    fontSize: '13px',
    fontWeight: 600,
  },
  itemList: {
    padding: '0 16px 16px',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 0',
  },
  divider: {
    borderBottom: '1px solid #f3f4f6',
  },
  rankCircle: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: 700,
    flexShrink: 0,
  },
  itemMiddle: {
    flex: 1,
    minWidth: 0,
  },
  itemCity: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#1f2937',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemField: {
    fontSize: '10px',
    color: '#9ca3af',
    marginTop: '1px',
  },
  itemRight: {
    textAlign: 'right',
    flexShrink: 0,
  },
  changePercent: {
    fontSize: '13px',
    fontWeight: 600,
  },
  changeDetail: {
    fontSize: '10px',
    color: '#9ca3af',
    marginTop: '1px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
    fontSize: '14px',
  },
  columnEmpty: {
    color: '#9ca3af',
    fontSize: '12px',
    textAlign: 'center',
    padding: '24px 16px',
  },
  volatilityRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '11px',
    color: '#6b7280',
    marginTop: '4px',
  },
  volatilityBarTrack: {
    height: '4px',
    background: '#f3f4f6',
    borderRadius: '2px',
    marginTop: '6px',
    overflow: 'hidden',
  },
  volatilityBarFill: {
    height: '100%',
    background: '#f59e0b',
    borderRadius: '2px',
  },
};

function formatPrice(value) {
  if (value == null) return '--';
  return `$${Number(value).toFixed(2)}`;
}

function formatPercent(value, showSign) {
  if (value == null) return '--';
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${Number(value).toFixed(1)}%`;
}

function IncreaseItem({ item, rank }) {
  return (
    <div style={styles.item}>
      <div
        style={{
          ...styles.rankCircle,
          background: '#dcfce7',
          color: '#166534',
        }}
      >
        {rank}
      </div>
      <div style={styles.itemMiddle}>
        <div style={styles.itemCity}>
          {item.city}, {item.state}
        </div>
        <div style={styles.itemField}>{item.field}</div>
      </div>
      <div style={styles.itemRight}>
        <div style={{ ...styles.changePercent, color: '#16a34a' }}>
          +{formatPercent(Math.abs(item.changePercent), false)}
        </div>
        <div style={styles.changeDetail}>
          {formatPrice(item.previousPrice)} &rarr; {formatPrice(item.currentPrice)}
        </div>
      </div>
    </div>
  );
}

function DecreaseItem({ item, rank }) {
  return (
    <div style={styles.item}>
      <div
        style={{
          ...styles.rankCircle,
          background: '#fee2e2',
          color: '#991b1b',
        }}
      >
        {rank}
      </div>
      <div style={styles.itemMiddle}>
        <div style={styles.itemCity}>
          {item.city}, {item.state}
        </div>
        <div style={styles.itemField}>{item.field}</div>
      </div>
      <div style={styles.itemRight}>
        <div style={{ ...styles.changePercent, color: '#dc2626' }}>
          {formatPercent(item.changePercent, false)}
        </div>
        <div style={styles.changeDetail}>
          {formatPrice(item.previousPrice)} &rarr; {formatPrice(item.currentPrice)}
        </div>
      </div>
    </div>
  );
}

function VolatileItem({ item, rank, maxVolatility }) {
  const barWidth = maxVolatility > 0 ? (item.volatility / maxVolatility) * 100 : 0;

  return (
    <div style={{ padding: '10px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div
          style={{
            ...styles.rankCircle,
            background: '#fef3c7',
            color: '#92400e',
          }}
        >
          {rank}
        </div>
        <div style={styles.itemMiddle}>
          <div style={styles.itemCity}>
            {item.city}, {item.state}
          </div>
          <div style={styles.volatilityRow}>
            <span>CV: {Number(item.volatility).toFixed(1)}%</span>
            <span>{item.priceRange}</span>
          </div>
          <div style={styles.volatilityBarTrack}>
            <div
              style={{
                ...styles.volatilityBarFill,
                width: `${barWidth}%`,
              }}
            />
          </div>
          <div
            style={{
              fontSize: '10px',
              color: '#9ca3af',
              marginTop: '3px',
            }}
          >
            {item.changeCount} distinct change{item.changeCount !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TopMovers({ data }) {
  if (!data) {
    return (
      <div style={styles.card}>
        <div style={styles.emptyState}>No price movement data available</div>
      </div>
    );
  }

  const { topIncreases = [], topDecreases = [], mostVolatile = [] } = data;
  const maxVolatility =
    mostVolatile.length > 0
      ? Math.max(...mostVolatile.map((v) => v.volatility))
      : 0;

  return (
    <div style={styles.card}>
      <div style={styles.title}>Top Movers</div>
      <div style={styles.subtitle}>
        Addresses with the most significant recent price changes
      </div>

      <div style={styles.grid}>
        {/* Column 1: Biggest Increases */}
        <div style={styles.column}>
          <div style={{ ...styles.columnBar, background: '#16a34a' }} />
          <div style={{ ...styles.columnHeader, color: '#16a34a' }}>
            Biggest Increases
          </div>
          <div style={styles.itemList}>
            {topIncreases.length === 0 ? (
              <div style={styles.columnEmpty}>No increases detected</div>
            ) : (
              topIncreases.slice(0, 5).map((item, idx) => (
                <React.Fragment key={item.addressId || idx}>
                  {idx > 0 && <div style={styles.divider} />}
                  <IncreaseItem item={item} rank={idx + 1} />
                </React.Fragment>
              ))
            )}
          </div>
        </div>

        {/* Column 2: Biggest Decreases */}
        <div style={styles.column}>
          <div style={{ ...styles.columnBar, background: '#dc2626' }} />
          <div style={{ ...styles.columnHeader, color: '#dc2626' }}>
            Biggest Decreases
          </div>
          <div style={styles.itemList}>
            {topDecreases.length === 0 ? (
              <div style={styles.columnEmpty}>No decreases detected</div>
            ) : (
              topDecreases.slice(0, 5).map((item, idx) => (
                <React.Fragment key={item.addressId || idx}>
                  {idx > 0 && <div style={styles.divider} />}
                  <DecreaseItem item={item} rank={idx + 1} />
                </React.Fragment>
              ))
            )}
          </div>
        </div>

        {/* Column 3: Most Volatile */}
        <div style={styles.column}>
          <div style={{ ...styles.columnBar, background: '#f59e0b' }} />
          <div style={{ ...styles.columnHeader, color: '#92400e' }}>
            Most Volatile
          </div>
          <div style={styles.itemList}>
            {mostVolatile.length === 0 ? (
              <div style={styles.columnEmpty}>No volatility data</div>
            ) : (
              mostVolatile.slice(0, 5).map((item, idx) => (
                <React.Fragment key={item.addressId || idx}>
                  {idx > 0 && <div style={styles.divider} />}
                  <VolatileItem
                    item={item}
                    rank={idx + 1}
                    maxVolatility={maxVolatility}
                  />
                </React.Fragment>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
